import React, { useState, useEffect, useCallback } from 'react';
import { styles } from './adminStyles';
import { theme } from '../../../theme';
import api from '../../../services/api';
import AdminBidDetails from './AdminBidDetails';

// Lists the latest version of every archived event; "Re-archive" re-runs the
// same backend pipeline to capture any post-closure bid overrides in a new
// version. The list/download logic here is self-contained (own api calls),
// but the "Manage Bids" modal needs the bid-override handlers and shared
// modal-bids state that live in AdminDashboard.js — passed down as props,
// same wiring AdminOverview.js's "View Bids" modal already uses.
const EventArchive = ({
    modalLiveBids, modalZones, modalBidsLoading, loadBidsForAuction,
    handleSaveDecision, handleDeleteBid,
    handleWithdrawWinner, handlePromoteBid, fetchPromotionHeadroom,
    fetchBackfillPreview, handleBackfillWinners
}) => {
    const [archives, setArchives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reArchivingId, setReArchivingId] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [viewingArchive, setViewingArchive] = useState(null);
    const [managingBidsArchive, setManagingBidsArchive] = useState(null);
    const [historyArchive, setHistoryArchive] = useState(null);
    const [historyVersions, setHistoryVersions] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const loadArchives = useCallback(async () => {
        try {
            const res = await api.get('/archives');
            setArchives(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadArchives(); }, [loadArchives]);

    const handleReArchive = async (originalEventId) => {
        setReArchivingId(originalEventId);
        try {
            await api.post(`/archives/${originalEventId}`);
            await loadArchives();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.msg || 'Re-archiving failed.');
        } finally {
            setReArchivingId(null);
        }
    };

    const openBidModal = (archive) => {
        setManagingBidsArchive(archive);
        loadBidsForAuction(archive.originalEventId);
    };

    const closeBidModal = () => setManagingBidsArchive(null);

    const openHistoryModal = async (archive) => {
        setHistoryArchive(archive);
        setHistoryLoading(true);
        try {
            const res = await api.get(`/archives/history/${archive.originalEventId}`);
            setHistoryVersions(res.data);
        } catch (err) {
            console.error(err);
            alert('Failed to load archive history.');
            setHistoryVersions([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const closeHistoryModal = () => {
        setHistoryArchive(null);
        setHistoryVersions([]);
    };

    const handleDownloadZip = async (archive) => {
        setDownloadingId(archive._id);
        try {
            const res = await api.get(`/archives/${archive._id}/zip`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `manual_proposals_${archive.originalEventId}_v${archive.version}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            alert('ZIP download failed.');
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <div style={{...styles.card, minHeight: '200px', marginBottom: 0}}>
            <h2 style={{marginTop: 0, ...styles.h2}}>Event Archive</h2>
            <p style={{margin: '0 0 15px', fontSize: '0.85rem', color: '#475569', fontFamily: '"Inter", sans-serif'}}>
                Retired events, kept here with a static report snapshot (and proposal ZIP, for Manual events) even if the original event is later deleted.
            </p>

            {loading ? <p style={styles.textMuted}>Loading archived events...</p> : (
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem'}}>
                    <thead>
                        <tr style={{backgroundColor: '#f0f2f5', borderBottom: '1px solid #ddd', textAlign: 'left'}}>
                            <th style={styles.th}>Event Name</th>
                            <th style={styles.th}>Version</th>
                            <th style={styles.th}>Closure</th>
                            <th style={styles.th}>Archived</th>
                            <th style={{...styles.th, textAlign: 'right'}}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {archives.map(archive => (
                            <tr key={archive._id} style={{borderBottom: '1px solid #eee'}}>
                                <td style={{...styles.td, fontWeight: 'bold'}}>{archive.eventName}</td>
                                <td style={styles.td}>
                                    v{archive.version}{archive.versionCount > 1 ? ` (of ${archive.versionCount})` : ''}
                                    {archive.bidsModifiedSinceArchive && (
                                        <div
                                            title="A bid on this event has been manually withdrawn/promoted since this snapshot was taken."
                                            style={{marginTop: '4px', display: 'inline-block', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: '#FFFFFF', color: theme.colors.accent, border: `1px solid ${theme.colors.accent}`}}
                                        >
                                            ⚠ Bids Modified — Re-Archive Required
                                        </div>
                                    )}
                                </td>
                                <td style={styles.td}>
                                    {archive.closureType === 'manual_early'
                                        ? <span title={archive.closureReason}>Closed Early</span>
                                        : 'Assessed'}
                                </td>
                                <td style={styles.td}>{new Date(archive.archivedAt).toLocaleString('en-GB')}</td>
                                <td style={{...styles.td, textAlign: 'right'}}>
                                    <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                                        <button onClick={() => setViewingArchive(archive)} style={{...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem'}}>
                                            View Report
                                        </button>
                                        {archive.versionCount > 1 && (
                                            <button
                                                onClick={() => openHistoryModal(archive)}
                                                title="View every previous version captured for this event"
                                                style={{...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem'}}
                                            >
                                                History ({archive.versionCount})
                                            </button>
                                        )}
                                        <button
                                            onClick={() => openBidModal(archive)}
                                            title="Open the live Bid Details view for this archived event — withdraw a winner, promote a standby bid, or run automated backfill."
                                            style={{...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem'}}
                                        >
                                            Manage Bids
                                        </button>
                                        {archive.zipFileKey && (
                                            <button
                                                onClick={() => handleDownloadZip(archive)}
                                                disabled={downloadingId === archive._id}
                                                style={{...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem', opacity: downloadingId === archive._id ? 0.6 : 1}}
                                            >
                                                {downloadingId === archive._id ? 'Preparing...' : 'Download ZIP'}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleReArchive(archive.originalEventId)}
                                            disabled={reArchivingId === archive.originalEventId || !archive.bidsModifiedSinceArchive}
                                            title={archive.bidsModifiedSinceArchive
                                                ? "Recapture the report (and ZIP, for Manual events) as a new version — use after a post-closure bid withdrawal or promotion."
                                                : "No bid changes detected since this snapshot — nothing new to capture."}
                                            style={archive.bidsModifiedSinceArchive
                                                ? {...styles.btnPrimary, backgroundColor: theme.colors.accent, padding: '6px 12px', fontSize: '0.8rem', opacity: reArchivingId === archive.originalEventId ? 0.6 : 1, cursor: reArchivingId === archive.originalEventId ? 'not-allowed' : 'pointer'}
                                                : {...styles.btnSecondary, padding: '6px 12px', fontSize: '0.8rem', opacity: 0.5, cursor: 'not-allowed'}}
                                        >
                                            {reArchivingId === archive.originalEventId ? 'Archiving...' : 'Re-archive'}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {archives.length === 0 && <tr><td colSpan="5" style={{padding: '20px', textAlign: 'center', color: '#475569'}}>No events have been archived yet.</td></tr>}
                    </tbody>
                </table>
            )}

            {viewingArchive && (
                <ArchiveReportModal archive={viewingArchive} onClose={() => setViewingArchive(null)} />
            )}

            {managingBidsArchive && (
                <div style={styles.overlay} onClick={closeBidModal}>
                    <div style={{...styles.modal, display: 'flex', flexDirection: 'column', overflow: 'hidden'}} onClick={(e) => e.stopPropagation()}>
                        <div style={{padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC', flexShrink: 0}}>
                            <h2 style={{margin: 0, ...styles.h2}}>Manage Bids — {managingBidsArchive.eventName} (Archived)</h2>
                            <button onClick={closeBidModal} style={{...styles.btnReversed, padding: '8px 16px'}}>Close ✕</button>
                        </div>
                        <div style={{flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: '#F8FAFC'}}>
                            <AdminBidDetails
                                auctionId={managingBidsArchive.originalEventId}
                                isModal
                                liveBids={modalLiveBids}
                                bidsLoading={modalBidsLoading}
                                zones={modalZones}
                                currentEv={{
                                    name: managingBidsArchive.eventName,
                                    status: 'closed',
                                    auctionType: managingBidsArchive.auctionType,
                                    endDate: managingBidsArchive.endDate
                                }}
                                isClosed
                                isManual={managingBidsArchive.auctionType === 'manual'}
                                handleSaveDecision={handleSaveDecision}
                                handleDeleteBid={handleDeleteBid}
                                handleWithdrawWinner={handleWithdrawWinner}
                                handlePromoteBid={handlePromoteBid}
                                fetchPromotionHeadroom={fetchPromotionHeadroom}
                                fetchBackfillPreview={fetchBackfillPreview}
                                handleBackfillWinners={handleBackfillWinners}
                            />
                        </div>
                    </div>
                </div>
            )}

            {historyArchive && (
                <div style={styles.overlay} onClick={closeHistoryModal}>
                    <div style={{...styles.modal, display: 'flex', flexDirection: 'column', overflow: 'hidden'}} onClick={(e) => e.stopPropagation()}>
                        <div style={{padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC', flexShrink: 0}}>
                            <h2 style={{margin: 0, ...styles.h2}}>{historyArchive.eventName} — Archive History</h2>
                            <button onClick={closeHistoryModal} style={{...styles.btnReversed, padding: '8px 16px'}}>Close ✕</button>
                        </div>
                        <div style={{flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: '#F8FAFC'}}>
                            {historyLoading ? <p style={styles.textMuted}>Loading versions...</p> : (
                                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', backgroundColor: '#FFFFFF'}}>
                                    <thead>
                                        <tr style={{backgroundColor: '#f0f2f5', borderBottom: '1px solid #ddd', textAlign: 'left'}}>
                                            <th style={styles.th}>Version</th>
                                            <th style={styles.th}>Closure</th>
                                            <th style={styles.th}>Archived</th>
                                            <th style={{...styles.th, textAlign: 'right'}}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyVersions.map(version => (
                                            <tr key={version._id} style={{borderBottom: '1px solid #eee'}}>
                                                <td style={{...styles.td, fontWeight: 'bold'}}>v{version.version}</td>
                                                <td style={styles.td}>
                                                    {version.closureType === 'manual_early'
                                                        ? <span title={version.closureReason}>Closed Early</span>
                                                        : 'Assessed'}
                                                </td>
                                                <td style={styles.td}>{new Date(version.archivedAt).toLocaleString('en-GB')}</td>
                                                <td style={{...styles.td, textAlign: 'right'}}>
                                                    <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
                                                        <button onClick={() => setViewingArchive(version)} style={{...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem'}}>
                                                            View Report
                                                        </button>
                                                        {version.zipFileKey && (
                                                            <button
                                                                onClick={() => handleDownloadZip(version)}
                                                                disabled={downloadingId === version._id}
                                                                style={{...styles.btnReversed, padding: '6px 12px', fontSize: '0.8rem', opacity: downloadingId === version._id ? 0.6 : 1}}
                                                            >
                                                                {downloadingId === version._id ? 'Preparing...' : 'Download ZIP'}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {historyVersions.length === 0 && <tr><td colSpan="4" style={{padding: '20px', textAlign: 'center', color: '#475569'}}>No versions found.</td></tr>}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Renders the frozen reportSnapshot (quickAuctionDetails() payload at archive
// time) — no API call needed, it's already fully self-contained JSON.
const ArchiveReportModal = ({ archive, onClose }) => {
    const summary = archive.reportSnapshot?.data?.summary || {};
    const bids = archive.reportSnapshot?.data?.bids || [];
    const humanizeKey = (key) => key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, s => s.toUpperCase());

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={{...styles.modal, display: 'flex', flexDirection: 'column', overflow: 'hidden'}} onClick={(e) => e.stopPropagation()}>
                <div style={{padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F8FAFC', flexShrink: 0}}>
                    <h2 style={{margin: 0, ...styles.h2}}>{archive.eventName} — v{archive.version} Report</h2>
                    <button onClick={onClose} style={{...styles.btnReversed, padding: '8px 16px'}}>Close ✕</button>
                </div>
                <div style={{flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: '#F8FAFC'}}>
                    <div style={{display: 'flex', gap: '24px', flexWrap: 'wrap', padding: '16px 20px', marginBottom: '15px', backgroundColor: '#FFFFFF', border: '2px solid #0D9488', borderRadius: '8px'}}>
                        {Object.entries(summary).map(([key, value]) => (
                            <div key={key} style={{display: 'flex', flexDirection: 'column'}}>
                                <div style={{fontSize: '0.7rem', textTransform: 'uppercase', color: '#475569', marginBottom: '2px'}}>{humanizeKey(key)}</div>
                                <div style={{fontSize: '1.1rem', fontWeight: 'bold', color: '#0F172A'}}>{value}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{overflow: 'auto', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#FFFFFF'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem'}}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Farmer</th>
                                    <th style={styles.th}>Action</th>
                                    <th style={styles.th}>Quantity</th>
                                    <th style={styles.th}>Price (£)</th>
                                    <th style={styles.th}>Payout (£)</th>
                                    <th style={styles.th}>BCR</th>
                                    <th style={styles.th}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bids.map(b => (
                                    <tr key={b.bidId}>
                                        <td style={styles.td}>{b.farmerName}</td>
                                        <td style={styles.td}>{b.actionName}</td>
                                        <td style={styles.td}>{b.quantity}</td>
                                        <td style={styles.td}>{b.price}</td>
                                        <td style={styles.td}>{b.payoutAmount}</td>
                                        <td style={styles.td}>{b.benefitCostRatio}</td>
                                        <td style={styles.td}>{b.status}</td>
                                    </tr>
                                ))}
                                {bids.length === 0 && <tr><td colSpan="7" style={{padding: '20px', textAlign: 'center', color: '#475569'}}>No bids recorded.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventArchive;
